/**
 * Inbox unread count for the layout header bell badge.
 *
 * Module-scoped intentionally — every layout copy needs the same value
 * and there's no per-request variance (the count is for the current
 * user, who is the same across every layout instance in one tab).
 * SSR-safe: state stays as `ref(0)` until the client mounts and the
 * first fetch runs.
 *
 * Polls every 5 MINUTES when active (was 60s previously — too noisy
 * for a low-volume transactional inbox). Stops when the document is
 * hidden (browser tab in background) to save needless requests, and
 * resumes immediately when the tab regains focus — which is when the
 * user actually cares about a fresh count anyway.
 *
 * The badge ALSO refreshes opportunistically on real user actions:
 *   * inbox page mount / folder switch (see pages/inbox.vue)
 *   * InboxNotificationsPopover open (see InboxNotificationsPopover.vue)
 *   * mark-as-read / mark-all-read / archive (manual refresh() calls)
 *
 * So the background poll is the safety net for "another user sent me a
 * message while I'm idling on /overview" — that's a low-frequency event
 * and 5 minutes is plenty of resolution for it.
 */
const unreadCount = ref(0)
const loading = ref(false)
let pollTimer: any = null
let visibilityListenerAttached = false

// Default poll interval for the background safety net. Chosen to keep
// idle traffic minimal — at 60s the original cadence we logged ~17
// requests per hour per active tab per user, which adds up quickly
// across the user base for what is effectively "is there new mail?"
// telemetry on an inbox that sees a few messages per day. The
// visibility-pause + user-action refreshes do most of the freshness
// work; the poll is just the floor.
const DEFAULT_POLL_INTERVAL_MS = 5 * 60 * 1000

async function fetchUnread() {
  if (typeof document !== 'undefined' && document.hidden) return  // skip when tab is in background
  loading.value = true
  try {
    const res: any = await $fetch('/api/inbox/unread-count', { credentials: 'include' })
    unreadCount.value = Number(res?.count ?? 0)
  } catch {
    // Silent — don't pop a toast for a background poll.
    // The badge stays at its last known value.
  } finally {
    loading.value = false
  }
}

function startPolling(intervalMs: number = DEFAULT_POLL_INTERVAL_MS) {
  if (typeof window === 'undefined') return
  stopPolling()
  pollTimer = setInterval(fetchUnread, intervalMs)
  if (!visibilityListenerAttached) {
    document.addEventListener('visibilitychange', () => {
      // When the user returns to the tab, immediately refresh —
      // they likely want the badge accurate after stepping away.
      if (!document.hidden) fetchUnread()
    })
    visibilityListenerAttached = true
  }
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

export function useInboxUnread() {
  return {
    unreadCount,
    loading,
    refresh: fetchUnread,
    startPolling,
    stopPolling
  }
}
